import React, { useEffect, useMemo, useState } from 'react';
import { Button, Divider, Flex, Modal, Segmented, Typography, message } from 'antd';
import { MinusCircleOutlined, PlusCircleOutlined, PlusOutlined, SettingOutlined } from '@ant-design/icons';
import * as actions from 'store/actions/AuthActions';
import { LeadTagSelect } from './leadTags';

const { Text } = Typography;

const plural = (count, word) => `${count} ${word}${count === 1 ? '' : 's'}`;

/**
 * Add tags to, or remove tags from, every selected lead in one request.
 *
 * Add: leads keep the tags they already have; typing a name that doesn't exist
 * offers to create it and pick it. Remove: only tags at least one selected
 * lead carries are offered, and a lead's other tags stay.
 *
 * `onApplied(leads)` gets each touched lead's new `tags` / `tags_detail` so the
 * table can patch its rows; `onManageTags` opens the tag manager, which this
 * modal replaces on the toolbar while leads are selected.
 */
const BulkEditLeadTagsModal = ({ open, onClose, leads = [], tags = [], onCreateTag, onApplied, onManageTags }) => {
  const [mode, setMode] = useState('add');
  const [tagIds, setTagIds] = useState([]);
  const [search, setSearch] = useState('');
  const [creating, setCreating] = useState(false);
  const [saving, setSaving] = useState(false);
  const removing = mode === 'remove';

  useEffect(() => {
    if (open) {
      setMode('add');
      setTagIds([]);
      setSearch('');
    }
  }, [open]);

  // Tags on at least one selected lead, with how many of them carry each.
  const carriedCount = useMemo(() => {
    const counts = new Map();
    leads.forEach((lead) => (lead.tags || []).forEach((id) => counts.set(id, (counts.get(id) || 0) + 1)));
    return counts;
  }, [leads]);
  const options = removing ? tags.filter((tag) => carriedCount.has(tag.id)) : tags;

  const term = search.trim();
  const exactMatch = tags.some((tag) => tag.name.toLowerCase() === term.toLowerCase());
  const canCreate = !removing && term && !exactMatch && onCreateTag;

  const switchMode = (next) => {
    setMode(next);
    setTagIds([]);
    setSearch('');
  };

  const createAndPick = async () => {
    if (!term || creating) return;
    setCreating(true);
    try {
      const tag = await onCreateTag(term);
      setTagIds((ids) => [...ids, tag.id]);
      setSearch('');
    } catch (e) {
      message.error(e?.response?.data?.name?.[0] || 'Could not create the tag');
    }
    setCreating(false);
  };

  const apply = async () => {
    setSaving(true);
    try {
      const leadIds = leads.map((lead) => lead.id);
      const tagCount = plural(tagIds.length, 'tag');
      const leadCount = plural(leads.length, 'lead');
      if (removing) {
        const result = await actions.bulkRemoveLeadTags(leadIds, tagIds);
        onApplied?.(result.leads);
        message.success(
          result.removed
            ? `Tags updated: removed ${tagCount} from ${leadCount}`
            : `None of the ${leadCount} had ${tagIds.length === 1 ? 'this tag' : 'these tags'}`
        );
      } else {
        const result = await actions.bulkAddLeadTags(leadIds, tagIds);
        onApplied?.(result.leads);
        message.success(
          result.added
            ? `Tags updated: added ${tagCount} to ${leadCount}`
            : `All ${leadCount} already had ${tagIds.length === 1 ? 'this tag' : 'these tags'}`
        );
      }
      onClose();
    } catch (e) {
      const data = e?.response?.data;
      message.error(
        data?.tag_ids?.[0] || data?.lead_ids?.[0] || `Could not ${removing ? 'remove' : 'add'} the tags`
      );
    }
    setSaving(false);
  };

  const leadCount = plural(leads.length, 'selected lead');

  return (
    <Modal
      open={open}
      onCancel={onClose}
      title={`${removing ? 'Remove tags from' : 'Add tags to'} ${leadCount}`}
      destroyOnHidden
      footer={
        <Flex justify="space-between" align="center">
          <Button type="link" icon={<SettingOutlined />} onClick={onManageTags} style={{ paddingInline: 0 }}>
            Manage tags
          </Button>
          <Flex gap="small">
            <Button onClick={onClose}>Cancel</Button>
            <Button
              type="primary"
              danger={removing}
              loading={saving}
              disabled={!tagIds.length}
              onClick={apply}
            >
              {removing ? 'Remove tags' : 'Add tags'}
            </Button>
          </Flex>
        </Flex>
      }
    >
      <Segmented
        block
        value={mode}
        onChange={switchMode}
        style={{ marginBottom: 12 }}
        options={[
          { value: 'add', label: 'Add tags', icon: <PlusCircleOutlined /> },
          { value: 'remove', label: 'Remove tags', icon: <MinusCircleOutlined /> },
        ]}
      />
      <LeadTagSelect
        key={mode}
        autoFocus
        tags={options}
        value={tagIds}
        onChange={(value) => setTagIds(value || [])}
        searchValue={search}
        onSearch={setSearch}
        placeholder={removing ? 'Pick the tags to remove' : 'Pick one or more tags'}
        notFoundContent={
          term ? null : removing ? 'None of the selected leads have tags' : 'No tags yet'
        }
        // Remove mode shows how many selected leads carry each tag; add mode
        // keeps LeadTagSelect's own chip rendering.
        {...(removing && {
          optionRender: (option) => {
            const tag = options.find((t) => t.id === option.value);
            return (
              <Flex justify="space-between" align="center">
                <span>
                  <span
                    style={{
                      display: 'inline-block', width: 8, height: 8, borderRadius: '50%',
                      background: tag?.color, marginInlineEnd: 6,
                    }}
                  />
                  {tag?.name}
                </span>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  on {carriedCount.get(option.value)} of {leads.length}
                </Text>
              </Flex>
            );
          },
        })}
        onInputKeyDown={(e) => {
          if (e.key === 'Enter' && canCreate) {
            e.preventDefault();
            createAndPick();
          }
        }}
        popupRender={(menu) => (
          <>
            {menu}
            {canCreate && (
              <>
                <Divider style={{ margin: '4px 0' }} />
                <Button
                  type="text"
                  block
                  icon={<PlusOutlined />}
                  loading={creating}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={createAndPick}
                  style={{ textAlign: 'left' }}
                >
                  Create "{term}"
                </Button>
              </>
            )}
          </>
        )}
      />
      <Text type="secondary" style={{ display: 'block', marginTop: 8, fontSize: 12 }}>
        {removing
          ? 'Only the picked tags are removed; each lead keeps its other tags.'
          : 'Leads keep the tags they already have; these are added on top.'}
      </Text>
    </Modal>
  );
};

export default BulkEditLeadTagsModal;
